import { CSS, Icons, keyframes, styled, theme } from '@arpeggi/ui'
import * as NavigationPrimitive from '@radix-ui/react-navigation-menu'
import { ComponentProps, forwardRef, PropsWithChildren } from 'react'
import { Link as RDOMLink } from 'react-router-dom'

const enterFromRight = keyframes({
  from: { transform: 'translateX(200px)', opacity: 0 },
  to: { transform: 'translateX(0)', opacity: 1 }
})

const enterFromLeft = keyframes({
  from: { transform: 'translateX(-200px)', opacity: 0 },
  to: { transform: 'translateX(0)', opacity: 1 }
})

const exitToRight = keyframes({
  from: { transform: 'translateX(0)', opacity: 1 },
  to: { transform: 'translateX(200px)', opacity: 0 }
})

const exitToLeft = keyframes({
  from: { transform: 'translateX(0)', opacity: 1 },
  to: { transform: 'translateX(-200px)', opacity: 0 }
})

const scaleIn = keyframes({
  from: { transform: 'rotateX(-30deg) scale(0.9)', opacity: 0 },
  to: { transform: 'rotateX(0deg) scale(1)', opacity: 1 }
})

const scaleOut = keyframes({
  from: { transform: 'rotateX(0deg) scale(1)', opacity: 1 },
  to: { transform: 'rotateX(-10deg) scale(0.95)', opacity: 0 }
})

const fadeIn = keyframes({
  from: { opacity: 0 },
  to: { opacity: 1 }
})

const fadeOut = keyframes({
  from: { opacity: 1 },
  to: { opacity: 0 }
})

const Root = styled(NavigationPrimitive.Root, {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  display: 'flex',
  justifyContent: 'center',
  zIndex: 20
})

const List = styled('div', {
  flex: 1,
  display: 'flex',
  position: 'relative',
  alignItems: 'center',
  margin: 0,
  listStyle: 'none',
  padding: 0
})

const Start = styled('div', {
  flexShrink: 0,
  display: 'flex',
  alignItems: 'stretch',
  gap: '40px',
  height: '100%'
})

const Menu = styled(NavigationPrimitive.List, {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '40px',
  margin: 0,
  width: '100vw',
  height: '64px',
  padding: '0 32px',
  boxSizing: 'border-box',
  background: '$gray1000',
  color: '$whiteA900'
})

const End = styled('div', {
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end'
})

const Item = NavigationPrimitive.Item

const itemStyles: CSS = {
  padding: '8px 12px',
  outline: 'none',
  userSelect: 'none',
  borderRadius: 4,
  fontSize: '14px',
  lineHeight: '20px',
  color: '$whiteA900',
  '&:focus': { boxShadow: `0 0 0 2px ${theme.colors.whiteA200.value}` },
  '&:hover': { backgroundColor: '$whiteA050' }
}

const Trigger = styled(NavigationPrimitive.Trigger, {
  all: 'unset',
  ...itemStyles,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2
})

const Caret = styled(Icons.CaretDown, {
  marginRight: '-8px',
  position: 'relative',
  top: 1,
  transition: 'transform 250ms ease',
  '[data-state=open] &': { transform: 'rotate(-180deg)' }
})

const Link = styled(NavigationPrimitive.Link, {
  ...itemStyles,
  display: 'block',
  textDecoration: 'none'
})

const Arrow = styled('div', {
  position: 'relative',
  top: '70%',
  backgroundColor: '$gray1000',
  width: 10,
  height: 10,
  transform: 'rotate(45deg)',
  borderTopLeftRadius: 2
})

const Indicator = styled(NavigationPrimitive.Indicator, {
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
  height: 10,
  top: '100%',
  overflow: 'hidden',
  zIndex: 1,
  transition: 'width, transform 250ms ease',
  '&[data-state="visible"]': { animation: `${fadeIn} 200ms ease` },
  '&[data-state="hidden"]': { animation: `${fadeOut} 200ms ease` }
})

const Content = styled(NavigationPrimitive.Content, {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  '@media only screen and (min-width: 600px)': { width: 'auto' },
  animationDuration: '250ms',
  animationTimingFunction: 'ease',
  '&[data-motion="from-start"]': { animationName: enterFromLeft },
  '&[data-motion="from-end"]': { animationName: enterFromRight },
  '&[data-motion="to-start"]': { animationName: exitToLeft },
  '&[data-motion="to-end"]': { animationName: exitToRight }
})

const SubList = styled('ul', {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '8px',
  margin: 0,
  listStyle: 'none'
})

const ListItemLink = styled(RDOMLink, {
  display: 'block',
  outline: 'none',
  textDecoration: 'none',
  userSelect: 'none',
  padding: '12px',
  borderRadius: 4,
  fontSize: '14px',
  lineHeight: 1,
  '&:focus': { boxShadow: `0 0 0 2px ${theme.colors.whiteA200.value}` },
  '&:hover': { backgroundColor: '$whiteA050' }
})

const ListItemHeading = styled('div', {
  fontWeight: 500,
  lineHeight: 1.2,
  marginBottom: 5,
  color: '$whiteA900'
})

const ListItemText = styled('p', {
  all: 'unset',
  color: '$whiteA500',
  lineHeight: 1.4,
  fontWeight: 'initial'
})

const ListItem = forwardRef<
  HTMLAnchorElement,
  PropsWithChildren<{ title: string } & ComponentProps<typeof RDOMLink>>
>(({ children, title, ...props }, ref) => (
  <NavigationPrimitive.Link asChild>
    <ListItemLink {...props} ref={ref}>
      <ListItemHeading>{title}</ListItemHeading>
      <ListItemText>{children}</ListItemText>
    </ListItemLink>
  </NavigationPrimitive.Link>
))

const ViewportPosition = styled('div', {
  position: 'absolute',
  display: 'flex',
  boxSizing: 'border-box',
  justifyContent: 'center',
  width: '100%',
  top: '100%',
  left: 0,
  perspective: '2000px'
})

const Viewport = styled(NavigationPrimitive.Viewport, {
  position: 'relative',
  transformOrigin: 'top center',
  marginTop: 10,
  width: '100%',
  backgroundColor: '$gray1000',
  borderRadius: 8,
  overflow: 'hidden',
  boxShadow: 'hsl(206 22% 7% / 35%) 0px 10px 38px -10px, hsl(206 22% 7% / 20%) 0px 10px 20px -15px',
  height: 'var(--radix-navigation-menu-viewport-height)',
  transition: 'width, height, 300ms ease',
  '&[data-state="open"]': { animation: `${scaleIn} 200ms ease` },
  '&[data-state="closed"]': { animation: `${scaleOut} 200ms ease` },
  '@media only screen and (min-width: 600px)': {
    width: 'var(--radix-navigation-menu-viewport-width)'
  }
})

export const Navbar = {
  Root,
  Menu,
  List,
  Start,
  End,
  Link,
  Trigger,
  Item,
  Caret,
  Arrow,
  Indicator,
  Content,
  SubList,
  ListItem,
  ListItemLink,
  ListItemHeading,
  ListItemText,
  ViewportPosition,
  Viewport
}
